import React, { useState, useEffect } from 'react';
import { ConfigProvider, Tag } from 'antd';
import { DeleteServico, GetAllServicos } from '@services/Servico/Index.jsx';
import FilterView from '@components/UI/FilterView/Index.jsx';
import { CustomLoader } from '@components/UI/CustomLoader/Index.jsx';
import { EditOptionComp } from '@components/UI/EditOption/Index.jsx';
import { DeleteOptionComp } from '@components/UI/DeleteOption/Index.jsx';
import ptBR from 'antd/es/locale/pt_BR';
import { StyledTable } from '@styles/datatables/datatable-antd.js';
import TextBlock from '@components/UI/TextBlock/Index..jsx';
import { flgStatusOptions } from '@enums/FlgStatus/Index';
import customStylesSelect from '@styles/react-select';
import Select from 'react-select';
import { flagAuditoriaOptions } from '@enums/FlagAuditoria/Index';

const GridServico = ({ state, setData, openModal, setSelectedRow, onDelete }) => {
  const [filters, setFilters] = useState({
    codigo: '',
    ano: '',
    servico: '',
    flagAuditoria: '',
    flgStatus: '',
  });
  const filterLabels = {
    codigo: 'CÓDIGO',
    ano: 'ANO',
    servico: 'SERVIÇO',
    flagAuditoria: 'É UMA AUDITORIA?',
    flgStatus: 'STATUS',
  };
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Busca os dados da API
  const fetchData = async (currentPage, currentRowsPerPage, filters = {}) => {
    setLoading(true);
    try {
      const response = await GetAllServicos(currentPage, currentRowsPerPage, filters);
      setData(response.items, response.totalItems);
    } catch (error) {
      console.error('Erro ao buscar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  // Atualiza os dados quando a página ou o número de linhas por página muda
  useEffect(() => {
    fetchData(page, rowsPerPage, filters);
  }, [page, rowsPerPage, filters]);

  // Manipula a mudança de página
  const handlePageChange = (newPage, newRowsPerPage) => {
    setPage(newPage);
    if (newRowsPerPage) {
      setRowsPerPage(newRowsPerPage);
    }
  };

  // Colunas da tabela
  const columns = [
    {
      title: 'CÓDIGO',
      dataIndex: 'codigo',
      key: 'codigo',
      width: '15%',
      sorter: (a, b) => a.codigo.localeCompare(b.codigo),
    },
    {
      title: 'ANO',
      dataIndex: 'ano',
      key: 'ano',
      width: '10%',
      sorter: (a, b) => a.ano.localeCompare(b.ano),
    },
    {
      title: 'SERVIÇO',
      dataIndex: 'servico',
      key: 'servico',
      width: '15%',
      sorter: (a, b) => a.servico.localeCompare(b.servico),
      render: (text, record) => (
        <TextBlock>
          {text}
        </TextBlock>
      ),
    },
    {
      title: 'É UMA AUDITORIA?',
      dataIndex: 'flagAuditoria',
      key: 'flagAuditoria',
      width: '15%',
      sorter: (a, b) => a.flagAuditoria - b.flagAuditoria,
      render: (flagAuditoria) => (
        <Tag color={flagAuditoria === true ? '#2b97be' : 'default'}>
          {(flagAuditoria) === true ? 'SIM' : 'NÃO'}
        </Tag>
      ),
    },
    {
      title: 'STATUS',
      dataIndex: 'flgStatus',
      key: 'flgStatus',
      width: '10%',
      sorter: (a, b) => a.flgStatus - b.flgStatus,
      render: (flgStatus) => (
        <Tag color={(flgStatus) === true ? 'green' : 'red'}>
          {(flgStatus) === true ? 'ATIVO' : 'INATIVO'}
        </Tag>
      ),
    },
    {
      title: 'AÇÕES',
      key: 'actions',
      width: '10%',
      render: (_, record) => (
        <div className="d-flex justify-content-center gap-3">
          <EditOptionComp
            row={record}
            setSelectedRow={setSelectedRow}
            openModal={openModal}
          />
          <DeleteOptionComp
            row={record}
            deleteFunction={DeleteServico}
            onDelete={onDelete}
            itemNameKey={"servico"}
          />
        </div>
      ),
    },
  ];

  const customFieldComponents = {
    flagAuditoria: ({ value, onChange }) => (
      <Select
        value={flagAuditoriaOptions.find(opt => opt.value === value)}
        onChange={option => onChange(option?.value ?? "")}
        placeholder={"Selecione uma opção"}
        options={flagAuditoriaOptions}
        isClearable
        isSearchable={false}
        styles={customStylesSelect}
      />
    ),
    flgStatus: ({ value, onChange }) => (
      <Select
        value={flgStatusOptions.find(opt => opt.value === value)}
        onChange={option => onChange(option?.value ?? "")}
        placeholder={"Selecione um status"}
        options={flgStatusOptions}
        isClearable
        isSearchable={false}
        styles={customStylesSelect}
      />
    ),
  };

  return (
    <div className='px-4'>

      <FilterView
        filters={filters}
        setFilters={setFilters}
        filterLabels={filterLabels}
        customFieldComponents={customFieldComponents}
      />

      <div className='mt-3 mb-3'>
        <h1>Lista de Serviços Registrados</h1>
      </div>

      <ConfigProvider locale={ptBR}>
        <StyledTable
          columns={columns}
          dataSource={state.data} // Passa os dados filtrados
          rowKey={"id"} // Chave única para cada linha
          loading={{
            spinning: loading,
            indicator: <CustomLoader />, // Spinner personalizado
          }}
          pagination={{
            current: page,
            pageSize: rowsPerPage,
            total: state.totalRows, // Total de registros
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            onChange: handlePageChange,
            onShowSizeChange: handlePageChange,
            showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} registros`, // Exibe o total de registros
          }}
          scroll={{ y: '20rem', x: 'max-content' }}
          locale={{
            emptyText: <h2 className='m-3'>Nenhum registro disponível para exibição.</h2>, // Mensagem quando não há dados
          }}
        />
      </ConfigProvider>
    </div>
  );
};

export default GridServico;